import * as APITypes from "~/API";
import { roundMoney } from "./money";

/** @deprecated Prefer roundMoney / formatMoney. Kept for call-site compatibility. */
export const convertCurrencyFromLowest = (
  amount?: number | null,
  _currency?: string,
) => roundMoney(amount);

/** @deprecated Prefer roundMoney. Kept for call-site compatibility. */
export const convertCurrencyToLowest = (
  amount?: number | string | null,
  _currency?: string,
) => roundMoney(amount);

export const getRate = (value: number, total: number) => {
  return total > 0 ? (value / total) * 100 : 0;
};

/**
 * Checks if a monthly report should be included in the yearly summary based on month filter.
 */
export const shouldIncludeMonthlyReport = (
  report: APITypes.MonthlyStatusReport,
  maxMonth?: number
): boolean => {
  if (maxMonth === undefined) {
    return true;
  }
  return report.month <= maxMonth;
};

/**
 * Creates an empty yearly status summary with default values.
 */
export const createEmptyYearlyStatusSummary = (
  ownerID: string,
  ownerType: APITypes.OwnerType,
  entityID: string,
  entityType: APITypes.EntityType,
  year: number
): APITypes.YearlyStatusSummary => {
  return {
    __typename: "YearlyStatusSummary",
    id: "",
    ownerID: ownerID,
    ownerType: ownerType,
    year: year,
    entityID: entityID,
    entityType: entityType,
    numOccupied: 0,
    numVacant: 0,
    numRenovation: 0,
    expectedRent: 0,
    expectedStayRevenue: 0,
    aggregateIncome: {
      __typename: "IncomeStatement",
      rentRevenue: 0,
      stayRevenue: 0,
      feeRevenue: 0,
      otherRevenue: 0,
      totalRevenue: 0,
      repairExpenses: 0,
      lostRentExpenses: 0,
      renovationExpenses: 0,
      totalExpenses: 0,
      net: 0,
      ownerID: ownerID,
      ownerType: ownerType,
    },
    createdAt: "",
    updatedAt: "",
    numUnits: 0,
    numBuildings: 0,
    numProperties: 0,
    numServiceRequests: 0,
    numAmenities: 0,
    numAmenityReservations: 0,
    _version: 1,
    _lastChangedAt: 1,
  };
};

/**
 * Aggregates occupancy and property metrics from a monthly report into the yearly summary.
 */
export const aggregateOccupancyMetrics = (
  yearlySummary: APITypes.YearlyStatusSummary,
  monthlyReport: APITypes.MonthlyStatusReport
): void => {
  yearlySummary.numOccupied += monthlyReport.numOccupied;
  yearlySummary.numVacant += monthlyReport.numVacant;
  yearlySummary.numRenovation += monthlyReport.numRenovation;
  yearlySummary.numUnits += monthlyReport.numUnits;
  yearlySummary.numBuildings += monthlyReport.numBuildings;
  yearlySummary.numProperties += monthlyReport.numProperties;
  yearlySummary.numServiceRequests += monthlyReport.numServiceRequests;
  yearlySummary.numAmenities += monthlyReport.numAmenities;
  yearlySummary.numAmenityReservations += monthlyReport.numAmenityReservations;
  yearlySummary.expectedRent += monthlyReport.expectedRent;
  yearlySummary.expectedStayRevenue =
    (yearlySummary.expectedStayRevenue ?? 0) +
    (monthlyReport.expectedStayRevenue ?? 0);
};

/**
 * Aggregates income data from a monthly report into the yearly summary.
 */
export const aggregateIncomeData = (
  yearlySummary: APITypes.YearlyStatusSummary,
  monthlyReport: APITypes.MonthlyStatusReport
): void => {
  yearlySummary.aggregateIncome.rentRevenue +=
    monthlyReport.aggregateIncome.rentRevenue;
  yearlySummary.aggregateIncome.stayRevenue =
    (yearlySummary.aggregateIncome.stayRevenue ?? 0) +
    (monthlyReport.aggregateIncome.stayRevenue ?? 0);
  yearlySummary.aggregateIncome.feeRevenue +=
    monthlyReport.aggregateIncome.feeRevenue;
  yearlySummary.aggregateIncome.otherRevenue +=
    monthlyReport.aggregateIncome.otherRevenue;
  yearlySummary.aggregateIncome.totalRevenue +=
    monthlyReport.aggregateIncome.totalRevenue;
  yearlySummary.aggregateIncome.repairExpenses +=
    monthlyReport.aggregateIncome.repairExpenses;
  yearlySummary.aggregateIncome.lostRentExpenses +=
    monthlyReport.aggregateIncome.lostRentExpenses;
  yearlySummary.aggregateIncome.renovationExpenses +=
    monthlyReport.aggregateIncome.renovationExpenses;
  yearlySummary.aggregateIncome.totalExpenses +=
    monthlyReport.aggregateIncome.totalExpenses;
  yearlySummary.aggregateIncome.net += monthlyReport.aggregateIncome.net;
};

/**
 * Aggregates all data from a single monthly report into the yearly summary.
 * This combines occupancy metrics and income data aggregation.
 */
export const aggregateMonthlyDataIntoYearlyStatus = (
  yearlySummary: APITypes.YearlyStatusSummary,
  monthlyReport: APITypes.MonthlyStatusReport
): void => {
  aggregateOccupancyMetrics(yearlySummary, monthlyReport);
  aggregateIncomeData(yearlySummary, monthlyReport);
};
